import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';
import Logo from '../components/Logo';
import ChangePasswordModal from '../components/ChangePasswordModal';
import AlertDialog from '../components/AlertDialog';

const InvestorNetworkInvestorDetail = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { tenantId, id } = useParams();
    const tenantPath = tenantId || user?.tenantIdentifier || 'default';

    const [loading, setLoading] = useState(true);
    const [investor, setInvestor] = useState(null);
    const [investments, setInvestments] = useState([]);
    const [error, setError] = useState('');
    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const sidebarOpen = sidebarExpanded;
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const profileMenuRef = useRef(null);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
                const listRes = await fetch(getApiUrl('users/my-network/investors'), { headers });
                if (!listRes.ok) throw new Error('Failed to load investors');
                const list = await listRes.json();
                const found = (list || []).find((i) => String(i.id) === String(id));
                if (!found) {
                    setError('Investor not found in your network');
                    setInvestor(null);
                    setInvestments([]);
                    return;
                }
                setInvestor(found);

                const invRes = await fetch(getApiUrl(`investments/investor/${id}`), { headers });
                if (invRes.ok) {
                    const invList = await invRes.json();
                    setInvestments(Array.isArray(invList) ? invList : []);
                } else {
                    setInvestments([]);
                }
            } catch (e) {
                console.error('Failed to load investor detail:', e);
                setError('Failed to load investor details');
            } finally {
                setLoading(false);
            }
        };
        if (user?.id) load();
    }, [id, user?.id]);

    const stats = useMemo(() => {
        const total = (investments || []).reduce((sum, inv) => sum + (inv.investmentAmount || 0), 0);
        const startups = new Set((investments || []).map((inv) => inv.startupId)).size;
        return { total, startups, investments: (investments || []).length };
    }, [investments]);

    const navItems = [
        { id: 'portfolio', label: 'Portfolio', path: `/${tenantPath}/investor-network`, icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
        { id: 'startups', label: 'Startups', path: `/${tenantPath}/investor-network/startups`, icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
        { id: 'investors', label: 'Investors', path: `/${tenantPath}/investor-network/investors`, icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
        { id: 'users', label: 'Management', path: `/${tenantPath}/investor-network/users`, icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
        { id: 'documents', label: 'Documents', path: `/${tenantPath}/investor-network/documents`, icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { id: 'activity', label: 'Activity', path: `/${tenantPath}/investor-network/activity`, icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' }
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };
        if (showProfileMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showProfileMenu]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50/80">
            <aside
                className={`fixed left-0 top-0 h-full z-40 flex flex-col bg-white/95 backdrop-blur-sm border-r border-slate-200/80 shadow-sm transition-[width] duration-200 ease-out ${sidebarOpen ? 'w-52' : 'w-[72px]'}`}
            >
                <div className="h-14 flex items-center shrink-0 px-3 border-b border-slate-100">
                    {sidebarOpen ? (
                        <Logo width={36} height={36} showText={true} className="[&_span]:!text-lg" />
                    ) : (
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">i</span>
                        </div>
                    )}
                </div>
                <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => navigate(item.path)}
                            title={item.label}
                            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${item.id === 'investors' ? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                        >
                            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                            </svg>
                            {sidebarOpen && <span className="text-sm truncate">{item.label}</span>}
                        </button>
                    ))}
                </nav>
                <div className="shrink-0 px-2 py-1 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={() => setSidebarExpanded((e) => !e)}
                        className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                        title={sidebarExpanded ? 'Collapse menu' : 'Expand menu'}
                    >
                        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sidebarExpanded ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
                        </svg>
                        {sidebarOpen && <span className="text-xs font-medium">{sidebarExpanded ? 'Collapse' : 'Expand'}</span>}
                    </button>
                </div>
                <div className="shrink-0 p-2 border-t border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-2">
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">{(user?.organizationName || user?.name || 'N')?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{user?.organizationName || user?.name}</p>
                                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            <main className={`transition-[margin] duration-200 ease-out ${sidebarOpen ? 'ml-52' : 'ml-[72px]'}`}>
                <header className="h-14 bg-white/80 backdrop-blur-sm border-b border-slate-200/80 flex items-center justify-between px-6 sticky top-0 z-30 gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <button
                            type="button"
                            onClick={() => navigate(`/${tenantPath}/investor-network/investors`)}
                            className="shrink-0 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                            ← Back to Investors
                        </button>
                        <h1 className="text-xl font-bold text-gray-900 truncate">Investor Details</h1>
                    </div>
                    <div className="flex items-center shrink-0">
                        <div className="relative" ref={profileMenuRef}>
                            <button
                                onClick={() => setShowProfileMenu(!showProfileMenu)}
                                className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg transition"
                            >
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                    <span className="text-white text-sm font-bold">{user?.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                            </button>
                            {showProfileMenu && (
                                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowChangePasswordModal(true);
                                            setShowProfileMenu(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                        Change password
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/${tenantPath}/settings`)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                        Settings
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div className="p-6 space-y-6">

                {error ? (
                    <div className="bg-white rounded-2xl border border-red-200 p-6 text-red-700">{error}</div>
                ) : (
                    <>
                        <div className="bg-white rounded-2xl border border-gray-200 p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow">
                                    <span className="text-white font-bold text-lg">{investor?.name?.charAt(0)?.toUpperCase() || 'I'}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-2xl font-bold text-gray-900">{investor?.name}</h1>
                                    <p className="text-gray-500">{investor?.email}</p>
                                    {investor?.jobTitle && <p className="text-sm text-gray-500 mt-1">{investor.jobTitle}</p>}
                                    {investor?.phoneNumber && <p className="text-sm text-gray-500 mt-1">{investor.phoneNumber}</p>}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                                <p className="text-xs text-gray-500 uppercase">Total invested</p>
                                <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(stats.total)}</p>
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                                <p className="text-xs text-gray-500 uppercase">Investments</p>
                                <p className="text-xl font-bold text-gray-900 mt-1">{stats.investments}</p>
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                                <p className="text-xs text-gray-500 uppercase">Startups</p>
                                <p className="text-xl font-bold text-gray-900 mt-1">{stats.startups}</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100">
                                <h2 className="text-lg font-semibold text-gray-900">Startup investments</h2>
                            </div>
                            {(investments || []).length === 0 ? (
                                <div className="p-8 text-center text-gray-500">No investments yet for this investor.</div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {(investments || []).map((inv) => {
                                        const company = (inv.startupCompanyName || '').trim();
                                        const founder = (inv.startupName || '').trim();
                                        const title = company || founder || 'Startup';
                                        const showFounder = Boolean(company && founder);
                                        return (
                                        <div key={inv.id} className="px-5 py-4 flex items-center justify-between gap-4">
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-900 truncate" title={title}>{title}</p>
                                                {showFounder ? (
                                                    <p className="text-sm text-gray-600 truncate">Founder: {founder}</p>
                                                ) : null}
                                                <p className={`text-sm text-gray-500 ${showFounder ? 'mt-0.5' : ''}`}>
                                                    {inv.investmentStage || 'Round N/A'} · {inv.status || 'ACTIVE'}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <p className="font-semibold text-gray-900">{formatCurrency(inv.investmentAmount)}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/${tenantPath}/investor-network/startups/${inv.startupId}`)}
                                                    className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg"
                                                >
                                                    View startup
                                                </button>
                                            </div>
                                        </div>
                                    );})}
                                </div>
                            )}
                        </div>
                    </>
                )}
                </div>
            </main>

            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />
            <ChangePasswordModal
                isOpen={showChangePasswordModal}
                onClose={() => setShowChangePasswordModal(false)}
                onSuccess={() =>
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: 'Password updated successfully.',
                        type: 'success'
                    })
                }
            />
        </div>
    );
};

export default InvestorNetworkInvestorDetail;

